import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Briefcase, FileText, Zap, Scale, FileSearch, Search, Clock, CheckCircle2, MessageSquare, Users, TrendingUp, Target, Sparkles, FlaskConical } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation Bar */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-full mb-4 sm:mb-6">
            <FileSearch className="h-8 w-8 sm:h-10 sm:w-10 text-primary-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 px-2">
            AI-Powered Document Intelligence
            <span className="block text-primary-600 mt-1 sm:mt-2">Transform Your Document Workflow</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
            Transform hours of document review into seconds. Our Solution automates document analysis, 
            content extraction, and intelligent search—so you can focus on what matters: making informed 
            decisions and driving your business forward.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            <Link
              to="/register"
              className="bg-primary-600 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </Link>
            <Link
              to="/how-to-use"
              className="bg-white text-primary-600 px-6 sm:px-10 py-3 sm:py-4 rounded-lg border-2 border-primary-600 hover:bg-primary-50 transition-colors font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-md"
            >
              See How It Works
            </Link>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-600 px-4">
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>Setup in Minutes</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
              <span>Built for Every Industry</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Stop Wasting Time on Manual Document Review
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                You know the pain: spending countless hours reviewing documents, contracts, reports, 
                and files. Your time is valuable, and your organization deserves faster, more thorough 
                document analysis.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                It eliminates the grunt work. Upload your documents, ask questions in plain 
                English, and get instant answers with precise citations. What used to take hours now takes 
                seconds—freeing you to focus on strategy, decision-making, and driving results.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Save 80% of Review Time</h3>
                    <p className="text-gray-600">Automate document analysis and focus on high-value work</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <TrendingUp className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Boost Productivity</h3>
                    <p className="text-gray-600">More time for strategy and decision-making means better outcomes</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Target className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Never Miss Critical Details</h3>
                    <p className="text-gray-600">AI-powered analysis catches what human eyes might miss</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What Teams Love About Our AI</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <Scale className="h-5 w-5 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Contract & Agreement Analysis</h4>
                      <p className="text-sm text-gray-600">Instantly identify key terms, risks, and obligations in any document</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Document Review</h4>
                      <p className="text-sm text-gray-600">Quickly extract key information, facts, and insights from large document sets</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <Search className="h-5 w-5 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Intelligent Search</h4>
                      <p className="text-sm text-gray-600">Find relevant information and patterns across your entire document library</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <MessageSquare className="h-5 w-5 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Content Extraction</h4>
                      <p className="text-sm text-gray-600">Extract specific data, insights, and answers from complex documents</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">Built for Every Industry</h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Every feature is designed to make your document work easier, faster, and more efficient
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Zap className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Lightning Fast Automation</h3>
            <p className="text-gray-600 leading-relaxed">
              Upload documents and get instant answers. No more hours spent searching through files. 
              Ask questions in natural language and receive precise answers with citations in seconds, 
              not hours.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <FileText className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Document Intelligence</h3>
            <p className="text-gray-600 leading-relaxed">
              Our AI understands document structure, context, and content. Upload 
              contracts, reports, agreements, policies, and any business documents. Get precise answers 
              with page numbers and document references for easy verification.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <MessageSquare className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Natural Language Queries</h3>
            <p className="text-gray-600 leading-relaxed">
              Ask questions like you're talking to a document assistant. "What are the key terms 
              in this contract?" or "Find all mentions of pricing in these documents." Get instant, 
              accurate answers with full citations—no complex search syntax needed.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Search className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Advanced Intelligent Search</h3>
            <p className="text-gray-600 leading-relaxed">
              Search across thousands of documents simultaneously. Identify patterns, 
              compare documents, and discover connections that would take days to find manually. 
              Semantic search understands context and meaning, not just keywords.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Users className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Team Collaboration</h3>
            <p className="text-gray-600 leading-relaxed">
              Share documents and research threads with your team. Organize by project, client, or 
              department. Maintain a searchable knowledge base of your organization's documents and insights. 
              Work together seamlessly on complex projects.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <Sparkles className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Smart Document Organization</h3>
            <p className="text-gray-600 leading-relaxed">
              Automatically organize documents by project, category, or client. Create folders, tag documents, 
              and build a searchable library of your work. Find what you need instantly, every time.
            </p>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section id="how-to-use" className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <BookOpen className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">How It Works</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in minutes. No technical expertise required—just upload and ask.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 px-4">
            <div className="text-center">
              <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Upload Your Documents</h3>
              <p className="text-gray-600 leading-relaxed">
                Upload contracts, reports, agreements, policies, or any business documents. Support for 
                PDFs, Word documents, and text files. Organize by project or category for easy access. The system 
                processes everything automatically.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-primary-600">2</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Ask Questions</h3>
              <p className="text-gray-600 leading-relaxed">
                Ask questions in natural language: "What are the key terms in this contract?" or "Find all 
                mentions of pricing in these documents." The AI understands context and 
                meaning—no training required.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-primary-600">3</span>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Get Instant Answers</h3>
              <p className="text-gray-600 leading-relaxed">
                Receive precise answers with exact citations, page numbers, and document references. Verify 
                every answer by clicking through to the source document. Make informed decisions with confidence and 
                save hours of manual research.
              </p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link
              to="/how-to-use"
              className="text-primary-600 hover:text-primary-700 font-semibold text-lg inline-flex items-center"
            >
              View Detailed Guide <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <Briefcase className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Use Cases</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              See how teams across industries use our AI to automate document work and drive results faster
            </p>
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-4">
            <Link to="/use-cases/contract-review" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <Scale className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Contract Review & Analysis</h3>
              <p className="text-gray-600">
                Quickly identify key terms, risks, obligations, and unusual clauses in contracts. Perfect for 
                M&A, commercial transactions, and employment agreements. Save hours on every contract review.
              </p>
            </Link>
            <Link to="/use-cases/document-analysis" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <FileSearch className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Document Analysis</h3>
              <p className="text-gray-600">
                Analyze reports, contracts, and business documents. Extract key information, identify patterns, 
                and gain insights faster. Make informed decisions with confidence.
              </p>
            </Link>
            <Link to="/use-cases/due-diligence" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <FileText className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Due Diligence</h3>
              <p className="text-gray-600">
                Accelerate due diligence reviews for transactions. Analyze hundreds of documents to identify 
                risks, liabilities, and deal-breakers efficiently. Complete reviews in days, not weeks.
              </p>
            </Link>
            <Link to="/use-cases/intelligent-search" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <Search className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Intelligent Search & Discovery</h3>
              <p className="text-gray-600">
                Search through your document library, reports, and research materials. Find relevant 
                information and patterns to support your decisions. Build on past insights.
              </p>
            </Link>
            <Link to="/use-cases/compliance" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <Briefcase className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Compliance & Regulatory Review</h3>
              <p className="text-gray-600">
                Review regulatory documents, compliance requirements, and policy changes. Stay ahead of 
                regulatory updates and ensure client compliance. Never miss a compliance deadline.
              </p>
            </Link>
            <Link to="/use-cases/policy-review" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <FileText className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Compliance & Policy Review</h3>
              <p className="text-gray-600">
                Review policies, compliance documents, and regulatory filings. Identify inconsistencies, verify requirements, 
                and ensure documents meet standards. Maintain compliance with precision and care.
              </p>
            </Link>
            <Link to="/use-cases/scientific-research" className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600">
              <FlaskConical className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Research & Analysis (Scientific)</h3>
              <p className="text-gray-600">
                Literature review, paper analysis, and hypothesis discovery with a private LLM. Keep sensitive research 
                on your infrastructure—ideal for labs, pharma, and academia.
              </p>
            </Link>
          </div>
          <div className="text-center mt-8">
            <Link
              to="/use-cases"
              className="text-primary-600 hover:text-primary-700 font-semibold text-lg inline-flex items-center"
            >
              View All Use Cases <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 py-12 sm:py-16 lg:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <Sparkles className="h-12 w-12 sm:h-16 sm:w-16 text-white mx-auto mb-4 sm:mb-6" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Why Teams Choose Us</h2>
            <p className="text-base sm:text-lg lg:text-xl text-primary-100 max-w-3xl mx-auto">
              Join professionals who are automating their document review and driving better results
            </p>
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 px-4">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <Clock className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-3">Save Time, Increase Productivity</h3>
              <p className="text-primary-100">
                Reduce document review time by 80%. Focus on high-value work and increase your 
                efficiency. More time for strategy means better outcomes.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <Zap className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-3">Instant Results</h3>
              <p className="text-primary-100">
                Get answers in seconds, not hours. Speed up decision-making, client responses, and 
                document analysis. Work faster without sacrificing quality.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
              <Target className="h-10 w-10 text-white mb-4" />
              <h3 className="text-xl font-semibold mb-3">Accurate Citations</h3>
              <p className="text-primary-100">
                Every answer includes precise citations with page numbers and document references. 
                Verify every claim and make decisions with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - links to Pricing page */}
      <section className="bg-white py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Ready to Get Started?</h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            From small teams to large enterprises, {config.appName} scales with your needs. See pricing and start your free trial.
          </p>
          <Link
            to="/pricing"
            className="inline-block bg-primary-600 text-white px-10 py-4 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
          >
            View Pricing & Plans <ArrowRight className="inline-block ml-2 h-5 w-5 align-middle" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Home;
